import applogger from '@applogger'
import debuglogger from 'src/debuglogger'
import logSomnambulistState from 'src/utils/logSomnambulistState'
import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import filestore from './filestore'
import config from './config'
import node from './node'
import blob from './blob'
import list from './list'
import user from './user'
import products from './products'
import cache from './cache'
import merge from 'lodash/merge'

import {GraphQLClient, UnknownServiceError} from './graphqlclient'
import { reset as resetTrackedProductIds, getTracked as getTrackedProductIds } from '../utils/productAccessTracker'

const inBrowser = () => typeof window !== 'undefined'

const normalizeObject = (data) => {
  if (!data) return {}
  if (Array.isArray(data)) return normalizeObject({ data })

  const normalized = {}
  for (const [key, value] of Object.entries(data)) {
    const valueType = Object.prototype.toString.call(value).slice(8, -1).toLowerCase()
    if (['null', 'undefined'].includes(valueType)) continue

    normalized[key] = value
    if (!['string', 'number', 'boolean'].includes(valueType)) normalized[key] = JSON.stringify(value)
  }

  return normalized
}

const state = () => ({
  registry: null,
  progressLoaderState: 0,
  editHeadManagment: false,
  flags: {
    'content-creator': false
  },
  messageHubReady: false,
  queuedErrors: [],
  adhocStorage: {},
  asynDataComponents: {},
  pageSettings: {},
  requestQueue: {},
  promises: {},
  logRocket: null
})

const mutations = {
  EDIT_HEAD_MANAGEMENT(state, value) {
    state.editHeadManagment = value
  },
  SET_PAGE_SETTING(state, value) {
    if (value && value.key) {
      Vue.set(state.pageSettings, value.key, value.value)
    }
  },
  DELETE_PAGE_SETTING(state, key) {
    if (key) {
      Vue.delete(state.pageSettings, key)
    }
  },
  STORE_ADHOC_STORAGE: function(state, value) {
    Vue.set(state.adhocStorage, value.key, value.data)
  },
  DELETE_ADHOC_STORAGE_ITEM(state, key) {
    if (key) {
      Vue.delete(state.adhocStorage, key)
    }
  },
  DELETE_MATCHING_ADHOC_STORAGE_ITEMS(state, value) {
    if (value) {
      Object.keys(state.adhocStorage).forEach(key => {
        if (key.includes(value)) {
          Vue.delete(state.adhocStorage, key)
        }
      })
    }
  },
  // SSR-only: shrinks the inlined initial catalog down to just the
  // product(s) a given render actually touched (tracked via
  // findProductByID/findByRoutePath/productsByItemID - see
  // utils/productAccessTracker.js), instead of shipping all ~531 products
  // on every page regardless of what's rendered. Called from
  // main-server.js's context.rendered hook, after Vue's renderToString has
  // already produced the HTML (so the rendered markup is unaffected) but
  // before ssrv2 serializes context.state for client hydration.
  //
  // adhocStorage.winecatalog (the ID-only catalog refs from the earlier
  // dedup work) is cleared, not shrunk, so winecatalog-preload-helper.js's
  // own "already loaded" skip-check (winecatalogRefs !== undefined) falls
  // through to a real fetch on the client instead of trusting a now-stale
  // ref list - that client-side fetch is what backfills the rest of the
  // catalog (lite immediately on hydration, full 30s later).
  PRUNE_UNREFERENCED_PRODUCTS(state) {
    const trackedIds = getTrackedProductIds(state.products)
    if (trackedIds.size === 0) return
    state.products.products = state.products.products.filter(product => trackedIds.has(product.id))
    Vue.delete(state.adhocStorage, 'winecatalog')
  },
  // content-creator.vue's created() hook (vuejs-dev/content-creator/
  // content-creator.vue) unconditionally merges default-settings.js's
  // defaultSystemCards (starter card templates for the widget customize
  // sidebar - "Wysiwyg Card", "Photo Card 1", etc) onto its own local,
  // fully-cloned widget data on every mount, regardless of what the loaded
  // blob already contains. Once any admin saves a blob through the editor,
  // that merged-in defaultSystemCards gets submitted back as part of the
  // full widget payload (see saveBlob in content-creator-mixin.js) and
  // persists in the backend from then on - so it round-trips on every
  // fetch even though only the editor UI (customize-sidebar-widget.vue)
  // and the save path ever read it. Confirmed unreferenced by every actual
  // display component (content-creator/fill/*.vue, content-creator/
  // layouts/*.vue). On allsaints this was 131KB across 18 blobs - 26% of
  // the entire initial state - for data no visitor's render path touches.
  //
  // Safe to strip unconditionally: main-server.js never populates
  // state.user with real auth (user/init's whole body is gated on
  // `typeof window !== 'undefined', so SSR has no session/JWT handling at
  // all), so the content-creator editing UI never renders server-side
  // regardless of who's actually requesting the page - and even if a
  // logged-in admin's browser then enables editing after hydration, this
  // same created() hook re-merges defaultSystemCards from default-settings.js
  // fresh, so the editor isn't left without card templates to work with.
  STRIP_EDITOR_ONLY_BLOB_DATA(state) {
    Object.values(state.blob.data).forEach(blob => {
      if (blob && blob.data && blob.data.defaultSystemCards) {
        Vue.delete(blob.data, 'defaultSystemCards')
      }
    })
  },
  RESET_PRODUCT_ACCESS_TRACKING(state) {
    resetTrackedProductIds(state.products)
  },
  SET_PROGRESS_LOADER: function(state, status) {
    if (status) {
      state.progressLoaderState += 1
    }
    else if (!status && state.progressLoaderState > 0) {
      state.progressLoaderState -= 1
    }
  },
  SET_GLOBAL_FLAG: function(state, {name, value}) {
    Vue.set(state.flags, name, value)
    if (inBrowser()) {
      localStorage.setItem('globalFlags', JSON.stringify(state.flags))
    }
  },
  RESTORE_GLOBAL_FLAGS: function(state, flags) {
    state.flags = flags
  },
  SET_MESASAGE_HUB_READY: function(state, val) {
    state.messageHubReady = val
  },
  SET_REGISTRY: function(state, registry) {
    state.registry = registry
  },
  REQUIRE_ASYNC_DATA: function(state, data) {
    if (state.asynDataComponents[data.id] === undefined) {
      state.asynDataComponents[data.id] = {
        widget: null,
        blob: blob
      }
    }
  },
  LOG_ROCKET: function(state, data) {
    state.logRocket = data
  }
}

const actions = {
  setLogRocket: function({ commit, state }, { logRocket, logRocketId, enabled }) {
    if (logRocket && logRocketId && enabled) {
      logRocket.init(logRocketId, {
        release: process.env.BUILDVERSION
      })
      console.info(`LogRocket Logging Intiailized -- ID ${logRocketId}`)
      commit('LOG_ROCKET', logRocket)
    }
  },
  logRocketIdentify: function({ state, getters }) {
    if (!state.logRocket) return

    const identity = getters.logRocketIdentity
    state.logRocket.identify(identity, {
      siteid: getters.getSiteId,
      sitename: state.registry.getServiceEndpoint('datastore'),
      buildVersion: process.env.BUILDVERSION,
      userid: state?.user?.JWTPayload?.user_id ?? 0
    })
    console.log(`LogRocket identified: ${identity}`)
  },
  logRocketTrack: function({ state }, { event, data }) {
    if (!state.logRocket) return
    state.logRocket.track(event ?? 'Unknown Event', normalizeObject(data))
  },
  logRocketError: function({ state }, { message, data }) {
    if (!state.logRocket) return
    state.logRocket.captureMessage(message ?? 'Unknown Error', { extra: normalizeObject(data) })
  },
  logRocketException: function({ state }, { exception, data }) {
    if (!state.logRocket) return
    state.logRocket.captureException(exception ?? new Error('Unknown Exception'), { extra: normalizeObject(data) })
  },
  promisePool: function({state}, {name, resolve}) {
    if (state.promises[name]) {
      return state.promises[name]
    }
    else {
      state.promises[name] = resolve.call()
        .finally(_ => {
          delete state.promises[name]
        })
      return state.promises[name]
    }
  },
  loadItemFromRemote: function({ dispatch }, { url, queryStr, config }) {
    if (typeof config === 'undefined') {
      throw new Error(`Config undefined, unable to dispatch request for ${url}`)
    }

    if (queryStr !== undefined) {
      const queryString = Object.entries(queryStr).map(([k, v]) => `${k}=${v}`).join('&')
      url = `${url}?${queryString}`
    }

    return dispatch('queuedAxiosRequest', { url, config })
      .then(resp => {
        if (resp.data.status) {
          return resp.data
        }
        else {
          const err = new Error(`Failed to retrieve item from remote: ${url}`)
          err.status = resp.status
          err.statusText = resp.statusText
          throw err
        }
      })
  },

  /**
 * This is a similar function to function __getrequest(url, headers, cacheParams) except it
 * does not perform an JWT verification and assumes that the query scope will always be the same
 * for all requests.
  */
  queuedAxiosRequest({ state }, { url, config }) {
    if (!state.requestQueue[url]) {
      state.requestQueue[url] = []
    }
    let p = new Promise((resolve, reject) => {
      state.requestQueue[url].push({resolve: resolve, reject: reject})
    })

    if (state.requestQueue[url].length === 1) {
      applogger.debug(`Performing axios actual request: ${url}`)
      axios.get(url, config)
        .then((response) => {
          applogger.debug('Received axios responses for: ', url)
          while (state.requestQueue[url].length) {
            state.requestQueue[url].pop().resolve(response)
          }
          return response
        })
        .catch((e) => {
          applogger.debug('Exception axois responses for: ', url)
          while (state.requestQueue[url].length) {
            state.requestQueue[url].pop().reject(e)
          }
          throw e
        })
    }
    return p
  },
  adhocstore: function({commit}, payload) {
    commit('STORE_ADHOC_STORAGE', payload)
  },
  pruneUnreferencedProducts: function({commit}) {
    if (process.env.SERVER_BUILD === 'ssr') {
      commit('PRUNE_UNREFERENCED_PRODUCTS')
    }
  },
  stripEditorOnlyBlobData: function({commit}) {
    if (process.env.SERVER_BUILD === 'ssr') {
      commit('STRIP_EDITOR_ONLY_BLOB_DATA')
    }
  },
  resetProductAccessTracking: function({commit}) {
    if (process.env.SERVER_BUILD === 'ssr') {
      commit('RESET_PRODUCT_ACCESS_TRACKING')
    }
  },
  reportError: function({ state, commit, dispatch }, payload) {
    if (payload && payload.type === 'error') {
      dispatch('logRocketError', { message: payload.title, data: payload })
    }
    if (state.messageHubReady) {
      Vue.prototype.$eventHub.$emit('statusMessage', payload)
    }
    else {
      state.queuedErrors.push(payload)
    }
  },

  messageHubReady: function({state, commit}, payload) {
    let errors = state.queuedErrors
    commit('SET_MESASAGE_HUB_READY', payload)

    state.queuedErrors = []
    errors.map(err => {
      Vue.prototype.$eventHub.$emit('statusMessage', err)
    })

    // This is a dirty, dirty hack to get the global flags to be loaded from local storage on a page refresh/rfeload
    if (inBrowser()) {
      try {
        let globalflags = JSON.parse(localStorage.getItem('globalFlags'))
        if (globalflags) {
          commit('RESTORE_GLOBAL_FLAGS', globalflags)
        }
      }
      catch (err) {
        // No-op
      }
    }
  },

  showProgressLoader: function({commit}) {
    commit('SET_PROGRESS_LOADER', true)
  },
  hideProgressLoader: function({commit}, payload) {
    if (payload && payload.force) {
      while (state.progressLoaderState > 0) {
        commit('SET_PROGRESS_LOADER', false)
      }
    }
    else {
      commit('SET_PROGRESS_LOADER', false)
    }
  },
  setGlobalFlag({commit}, payload) {
    commit('SET_GLOBAL_FLAG', payload)
  },
  generalReset: function({state}, payload = false) {
    return Promise.resolve(true)
      .then(() => {
        if (inBrowser()) {
          // Grab all the items in the localStorage that we want to retain between login and logout
          const itemsToSave = [
            '/vuejs/config',
            '/vuejs/taxonomy',
            '/vuejs/field-info',
            'ecommerce_configuration',
            'IncipereVersion',
            'cart_cookie_id',
            'incipere-debug',
            'AnonPermissions',
            'DEBUGUsername',
            'DEBUGPassword'
          ]

          // Do not add these items if the reset is run via logout.
          if (!payload) {
            itemsToSave.push('somnambulistSelectedOrder')
          }

          let savedItems = {}
          for (const item of itemsToSave) {
            savedItems[item] = localStorage.getItem(item)
          }

          // Clear local storage to remove all the user cached items
          localStorage.clear()

          // Restores the configuration Items that we want retained.

          Object.keys(savedItems).map(cacheItem => {
            if (savedItems[cacheItem]) {
              localStorage.setItem(cacheItem, savedItems[cacheItem])
            }
          })
        }
      })
      .then(() => {
        let promises = [
          this.dispatch('node/resetCache'),
          this.dispatch('list/resetCache')
        ]
        if (state.ecommerce !== undefined) {
          promises.push(this.dispatch('ecommerce/resetCache'))
        }
        return Promise.all(promises)
      })
  },
  checkAccess: function({state}, payload) {
    throw new Error('checkAccess is deprecated in favour or the user/itemaccess action and getter')
  }
}

const getters = {
  logRocketIdentity(state, getters) {
    const prefix = []
    if (state.registry.getSiteSetting('logRocketIdentityPrefix')) {
      prefix.push(state.registry.getSiteSetting('logRocketIdentityPrefix') + '--')
    }

    prefix.push(getters.getSiteId)
    prefix.push(state.registry.getServiceEndpoint('datastore'))
    prefix.push(state?.user?.JWTPayload?.user_id ?? 0)
    return prefix.filter(p => p).join('::')
  },
  AuthorizedRequestConfig(state, getters) {
    return (config) => {
      let defatultAXIOSConfig = {
        baseURL: state.registry.getServiceEndpoint('datastore'),
        timeout: 120000,
        maxRedirects: 3,
        withCredentials: false,
        validateStatus: function(status) {
          return (status >= 200 && status < 300) || status === 304
        },
        headers: {}
      }
      // Combine the default cache options with those provided.
      config = merge(defatultAXIOSConfig, config)

      if (!config.noauth) {
        let jwt = getters['user/verifiedJWT']
        if (jwt) {
          config.headers.authorization = `Bearer ${jwt}`
        }
      }
      return config
    }
  },
  getAdHocStorageValue(state) {
    return (key) => {
      return state.adhocStorage[key]
    }
  },
  getServiceEndpoint(state) {
    return (service) => {
      return state.registry.getServiceEndpoint(service)
    }
  },
  registryFeature(state) {
    return (featureName) => {
      return state.registry.getFeature(featureName)
    }
  },
  getModuleSetting(state) {
    return (moduleName, setting, dflt) => {
      return state.registry.getModuleSetting(moduleName, setting, dflt)
    }
  },
  getSiteSetting(state) {
    return (setting, dflt) => {
      return state.registry.getSiteSetting(setting, dflt)
    }
  },
  getSiteId(state) {
    return state.registry.getSiteId()
  },
  getEcommerceEnabled: function(state) {
    return state.registry.getFeature('ecommerce')
  },
  getGraphQLClient: function(state, _getters, _rootState, rootGetters) {
    return (service, queryDoc) => {
      let uri = state.registry.getServiceEndpoint('datastore') + '/graphql'
      if (service) {
        uri = state.registry.getServiceEndpoint(service)
      }
      if (!uri) {
        throw new UnknownServiceError(`Unknow Service: ${service}`)
      }
      let client = new GraphQLClient(uri, queryDoc)
        .onQuery(context => {
          let jwt = rootGetters['user/JWT']
          return {
            ...context,
            headers: {
              ...context.headers,
              Authorization: jwt ? `Bearer ${jwt}` : null,
              'X-Site-ID': state.registry.getSiteId()
            }
          }
        })
        // if (inBrowser() && !service) {
        //   if ( window.Cypress) {
        //     client.onQuery(context => {
        //       return {
        //         ...context,
        //         headers: {
        //           ...context.headers,
        //           'X-Aspedia-Monkey-Wrench': 1
        //         }
        //       }
        //     })
        //   }
        // }
      return client
    }
  }
}

export default function(registry) {
  Vue.use(Vuex)
  let store = new Vuex.Store({
    actions: actions,
    state: state,
    getters: getters,
    mutations: mutations,
    modules: {
      config,
      node,
      blob,
      user,
      list,
      filestore,
      cache,
      products
    }
  })

  if (!inBrowser() || !window.__INITIAL_STATE__) {
    store.commit('SET_REGISTRY', registry)
  }

  // Prime the store with server-initialized state.
  // the state is determined during SSR and inlined in the page markup as a
  // plain object literal (window.__INITIAL_STATE__={...}) - see
  // vuejs/app/website/main-server.js for why this is no longer
  // LZString-compressed.
  if (inBrowser() && window.__INITIAL_STATE__) {
    try {
      const initialState = window.__INITIAL_STATE__
      // Log before any mutation - initialState.registry = registry just
      // below is a client-side addition SSR never shipped, and
      // state.products.products gets wholesale-replaced shortly after by
      // the lite-catalogue hydration fetch (SET_PRODUCTS in
      // winelist-async-loader.js's storeProducts dispatch) - since
      // window.__INITIAL_STATE__ is the same object reference the store
      // becomes reactive around (not a clone), reading it here is the only
      // point that reflects exactly what SSR actually sent over the wire.
      // Guarded directly, not just relying on debuglogger's internal no-op -
      // JSON.stringify(initialState) is real, non-trivial work Terser can't
      // prove is discarded just by looking at the debuglogger.log(...) call
      // site, so it would otherwise still run on every hydration even with
      // DEBUG_LOGGING off.
      if (process.env.DEBUG_LOGGING) {
        const ssrProductCount = (initialState.products && initialState.products.products) ? initialState.products.products.length : 0
        debuglogger.log(`[hydration] SSR initial state (pre-lite-fetch): ${ssrProductCount} products, ${JSON.stringify(initialState).length} bytes`)
        // somnambulistui's install() registers the module eagerly for
        // every SSR render (lazyStore: process.env.SERVER_BUILD !== 'ssr'
        // evaluates false server-side), but its data (orderTypes etc.)
        // only gets populated where the /wine-club route's meta.onload
        // actually dispatches somnambulistui/init - so it's present but
        // empty on every other page, not absent.
        logSomnambulistState(initialState.somnambulistui, 'SSR pre-lite-fetch')
      }
      initialState.registry = registry
      store.replaceState(initialState)
    }
    catch {
      // Malformed/partial initial state - fall back to the client's own bootstrap fetches.
    }
  }

  if (typeof window !== 'undefined' && typeof window.Cypress !== 'undefined') {
    window.__store__ = store
  }

  return store
}
